import { Component } from '@theme/component';

// https://developers.klaviyo.com/en/reference/create_client_event
// https://developers.klaviyo.com/en/reference/create_client_subscription
const KLAVIYO_REVISION = '2026-04-15';
const KLAVIYO_EVENT_URL = 'https://a.klaviyo.com/client/events';
const KLAVIYO_SUBSCRIBE_URL = 'https://a.klaviyo.com/client/subscriptions';

/** Delay before a `data-auto` step advances after a pick, so the selected state registers. */
const AUTO_ADVANCE_DELAY = 350;

// TODO(dev): temporary preview mode — set to false (or delete, along with
// #previewSubmission and its branch in #submit) before launch. While true,
// nothing is sent to Klaviyo; the success message instead shows exactly
// what would have been submitted (event, list, profile, answers).
const PREVIEW_MODE = false;

/**
 * A stepped, one-question-at-a-time kit-design survey. The flow is declared
 * entirely in the block's markup — this class is just the engine:
 *
 * - `[data-step]` elements are the screens; one is visible at a time.
 * - `[data-when="name:value"]` steps only count while the named form field
 *   holds that value (conditional sizing steps), so the progress bar and
 *   counter always reflect the visitor's actual remaining path.
 * - `[data-auto]` steps (single radio questions) advance on their own
 *   shortly after a pick; other steps use the Continue button, which turns
 *   into the submit button on the final step.
 *
 * `interest-signup` upstream creates the Klaviyo profile and hands off here
 * with the visitor's name/email as URL params, which pre-fill hidden inputs
 * and reveal a personalized greeting. If either param is missing — e.g. a
 * visitor lands here directly — the first step asks for name/email visibly
 * instead (same field names), so the rest of the flow behaves identically.
 * On submit: subscribes the visitor to a Klaviyo list (if a List ID is
 * configured — skipped silently otherwise) and fires a single
 * survey-completed event with every answer mirrored on the profile as
 * custom properties. Email is the one field Klaviyo requires, so submission
 * blocks with a friendly error if it's still missing. No Shopify customer is
 * created — Klaviyo's Shopify integration syncs profiles.
 *
 * @typedef {Object} KitSurveyFormRefs
 * @property {HTMLFormElement} form
 * @property {HTMLElement} greeting
 * @property {HTMLInputElement} [firstNameInput]
 * @property {HTMLInputElement} [lastNameInput]
 * @property {HTMLInputElement} emailInput
 * @property {HTMLElement} progressBar
 * @property {HTMLElement} counter
 * @property {HTMLButtonElement} backButton
 * @property {HTMLButtonElement} nextButton
 * @property {HTMLElement} nextLabel
 * @property {HTMLElement} successMessage
 * @property {HTMLElement} successHeading
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} errorMessageText
 *
 * @extends {Component<KitSurveyFormRefs>}
 */
class KitSurveyForm extends Component {
  requiredRefs = [
    'form',
    'greeting',
    'emailInput',
    'progressBar',
    'counter',
    'backButton',
    'nextButton',
    'nextLabel',
    'successMessage',
    'successHeading',
    'errorMessage',
    'errorMessageText',
  ];

  #index = 0;

  /**
   * Full name for personalization: first name from the interest-signup
   * hand-off (URL param) concatenated with last name, whichever are known —
   * the hand-off itself never includes a last name, so this is empty/first-
   * only until the visitor fills in the identity step (if shown). Read live
   * (not cached) so the greeting and success heading pick up freshly-typed
   * names immediately.
   */
  get #name() {
    const { firstNameInput, lastNameInput } = this.refs;
    const first = firstNameInput instanceof HTMLInputElement ? firstNameInput.value.trim() : '';
    const last = lastNameInput instanceof HTMLInputElement ? lastNameInput.value.trim() : '';
    return [first, last].filter(Boolean).join(' ');
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #autoAdvanceTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#greetFromUrlParams();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#autoAdvanceTimer);
  }

  /**
   * Steps currently in the visitor's path: every `[data-step]` whose
   * `data-when="name:value"` condition (if any) matches the form's current
   * values.
   *
   * @returns {HTMLElement[]}
   */
  get #steps() {
    const formData = new FormData(this.refs.form);

    return [...this.querySelectorAll('[data-step]')].filter((step) => {
      if (!(step instanceof HTMLElement)) return false;
      const when = step.dataset.when;
      if (!when) return true;
      // "name:value" or "name:value1,value2" — eligible if the named field
      // currently holds any of the listed values.
      const [name, values] = when.split(':');
      return (values ?? '').split(',').includes(String(formData.get(name ?? '') || ''));
    });
  }

  /**
   * Bound via `on:change` on the form. Keeps the progress total in sync
   * (answers can add or remove conditional steps) and auto-advances
   * `data-auto` steps after a pick.
   */
  handleChange = () => {
    this.#render();

    const step = this.#steps[this.#index];
    if (!step?.hasAttribute('data-auto')) return;

    clearTimeout(this.#autoAdvanceTimer);
    this.#autoAdvanceTimer = setTimeout(() => this.#go(1), AUTO_ADVANCE_DELAY);
  };

  /** Bound via `on:click` on the back button. */
  back = () => this.#go(-1);

  /**
   * Bound via `on:submit` on the form. The Continue button is the form's
   * submit button, so Enter and click both land here: advance until the
   * final step, then actually submit to Klaviyo.
   *
   * @param {SubmitEvent} event
   */
  handleSubmit = (event) => {
    event.preventDefault();

    if (this.#index < this.#steps.length - 1) {
      this.#go(1);
      return;
    }

    this.#submit();
  };

  /**
   * Moves through the eligible steps, validating the current step's inputs
   * before moving forward.
   *
   * @param {number} delta
   */
  #go(delta) {
    clearTimeout(this.#autoAdvanceTimer);

    const steps = this.#steps;
    const current = steps[this.#index];

    if (delta > 0 && current && !this.#validateStep(current)) return;

    this.#index = Math.min(Math.max(this.#index + delta, 0), steps.length - 1);
    this.#render(true);
  }

  /**
   * @param {HTMLElement} step
   * @returns {boolean}
   */
  #validateStep(step) {
    for (const control of step.querySelectorAll('input, textarea')) {
      if (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement) {
        if (!control.reportValidity()) return false;
      }
    }
    return true;
  }

  /**
   * Single source of truth for the UI: shows the current step, sizes the
   * progress bar, updates the counter, and swaps the Continue button's
   * label to the submit label on the final step.
   *
   * @param {boolean} [focus] - move focus to the step (skipped on initial render)
   */
  #render(focus = false) {
    const steps = this.#steps;
    this.#index = Math.min(this.#index, steps.length - 1);
    const current = steps[this.#index];

    for (const step of this.querySelectorAll('[data-step]')) {
      step.toggleAttribute('hidden', step !== current);
    }

    const { progressBar, counter, backButton, nextButton, nextLabel } = this.refs;
    const isLast = this.#index === steps.length - 1;

    progressBar.style.width = `${((this.#index + 1) / steps.length) * 100}%`;
    counter.textContent = `${this.#index + 1} / ${steps.length}`;
    backButton.hidden = this.#index === 0;
    nextLabel.textContent = isLast ? nextButton.dataset.submitLabel ?? '' : nextButton.dataset.nextLabel ?? '';

    this.#updateGreeting(current);

    if (focus && current instanceof HTMLElement) current.focus({ preventScroll: true });
  }

  /**
   * Fills the `[name]` placeholder from the signup hand-off, or drops it
   * gracefully (with any preceding comma) when there's no name.
   *
   * @param {string} template
   * @returns {string}
   */
  #fillTemplate(template) {
    return this.#name
      ? template.replaceAll('[name]', this.#name)
      : template.replace(/,? \[name\]/, '').replace('[name]', '').trim();
  }

  /**
   * Swaps the greeting heading to the current step's `data-greeting` (falling
   * back to the block-level default), filling the `[name]` placeholder from
   * the signup hand-off or dropping it gracefully. Replays the entry
   * animation when the text actually changes.
   *
   * @param {HTMLElement | undefined} step
   */
  #updateGreeting(step) {
    const { greeting } = this.refs;
    const template = step?.dataset.greeting || greeting.dataset.greetingTemplate || '';
    const text = this.#fillTemplate(template);

    greeting.classList.toggle('hidden', !text);
    if (greeting.textContent === text) return;

    greeting.textContent = text;
    greeting.style.animation = 'none';
    void greeting.offsetWidth; // restart the entry animation
    greeting.style.animation = '';
  }

  /**
   * Pre-fills name/email from the interest-signup hand-off (URL params). If
   * an email came through, the identity step (which would otherwise ask for
   * it) is dropped from the flow outright — a one-time decision made before
   * the first `#render()`, not a reactive `data-when` condition, so later
   * edits to the email field can't retroactively yank the step out from
   * under the visitor while they're still on it.
   *
   * Only the step's `data-step` marker is removed, not the step itself —
   * `emailInput` is a required ref, so its element has to stay put in the
   * DOM (just permanently `hidden`, as it already is in the markup) rather
   * than being deleted, or a later ref re-scan would throw looking for it.
   */
  #greetFromUrlParams() {
    const params = new URLSearchParams(window.location.search);
    // The signup form only collects a single "first name" field, so that's
    // all we ever have to hand off here — there's no last name to prefill.
    const name = params.get('name');
    const email = params.get('email');

    const { firstNameInput, emailInput } = this.refs;

    if (name && firstNameInput instanceof HTMLInputElement && !firstNameInput.value) {
      firstNameInput.value = name;
    }

    if (email) {
      if (emailInput instanceof HTMLInputElement && !emailInput.value) emailInput.value = email;
      this.querySelector('[data-identity-step]')?.removeAttribute('data-step');
    }
  }

  async #submit() {
    const { form, emailInput, nextButton, successMessage, errorMessage } = this.refs;

    // The identity step's own `required` validation (via #validateStep)
    // already covers the visitor-facing path, but the field also stays
    // hidden (never asked) when the hand-off already supplied an email —
    // so this is a last-resort check that Klaviyo can't accept the
    // event/profile write without it.
    // (Skipped in preview mode so the flow can be tested from a direct
    // page load with no hand-off params.)
    if (!emailInput.value && !PREVIEW_MODE) {
      this.#showError(
        "We couldn't find your email. Please use the link from your original signup to reach this page."
      );
      return;
    }

    const publicApiKey = this.dataset.klaviyoPublicApiKey;

    if (!publicApiKey && !PREVIEW_MODE) {
      console.error('kit-survey-form: missing a Klaviyo public API key. Set it in Theme settings → Klaviyo.');
      this.#showError();
      return;
    }

    errorMessage.classList.add('hidden');
    successMessage.classList.add('hidden');
    nextButton.disabled = true;

    const formData = new FormData(form);

    /** @type {Record<string, unknown>} */
    const profileAttributes = { email: emailInput.value };

    const firstName = String(formData.get('first_name') || '').trim();
    if (firstName) profileAttributes.first_name = firstName;

    const lastName = String(formData.get('last_name') || '').trim();
    if (lastName) profileAttributes.last_name = lastName;

    const phone = String(formData.get('phone') || '').trim();
    if (phone) profileAttributes.phone_number = phone;

    // Address fields (street/city/state/ZIP) are the survey's final step.
    // Only fields the visitor actually filled in are sent, mapped onto
    // Klaviyo's profile `location` properties.
    /** @type {Record<string, string>} */
    const location = {};
    for (const field of ['address1', 'city', 'region', 'zip']) {
      const value = String(formData.get(field) || '').trim();
      if (value) location[field] = value;
    }
    if (Object.keys(location).length > 0) profileAttributes.location = location;

    const surveyAnswers = this.#collectSurveyAnswers(formData);
    const profileProperties = this.#omitEmptyValues(surveyAnswers);
    if (Object.keys(profileProperties).length > 0) {
      profileAttributes.properties = profileProperties;
    }

    if (PREVIEW_MODE) {
      this.#previewSubmission(profileAttributes, surveyAnswers);
      return;
    }

    this.#subscribeToList(profileAttributes);

    const body = {
      data: {
        type: 'event',
        attributes: {
          properties: surveyAnswers,
          metric: {
            data: {
              type: 'metric',
              attributes: {
                name: this.dataset.klaviyoMetricName || 'Completed Kit Survey',
              },
            },
          },
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes,
            },
          },
        },
      },
    };

    try {
      const response = await fetch(`${KLAVIYO_EVENT_URL}?company_id=${encodeURIComponent(publicApiKey)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(body),
      });

      if (response.status === 202) {
        this.#showSuccessPane();
        return;
      }

      this.#showError(await this.#parseErrorDetail(response));
    } catch (error) {
      this.#showError();
    } finally {
      nextButton.disabled = false;
    }
  }

  /**
   * TODO(dev): temporary — remove with PREVIEW_MODE. Shows the success state
   * with a dump of exactly what would have been sent to Klaviyo: the event
   * name, list handling, profile attributes (answers mirrored as custom
   * properties), and the event's answer properties.
   *
   * @param {Record<string, unknown>} profileAttributes
   * @param {Record<string, unknown>} surveyAnswers
   */
  #previewSubmission(profileAttributes, surveyAnswers) {
    const { successMessage, nextButton } = this.refs;

    const payload = {
      event: this.dataset.klaviyoMetricName || 'Completed Kit Survey',
      list: this.dataset.klaviyoListId || '(none — list subscribe would be skipped)',
      profile: profileAttributes,
      eventProperties: surveyAnswers,
    };

    let preview = successMessage.querySelector('pre');
    if (!preview) {
      preview = document.createElement('pre');
      preview.className = 'kit-survey__preview';
      successMessage.append(preview);
    }
    preview.textContent = JSON.stringify(payload, null, 2);

    nextButton.disabled = false;
    this.#showSuccessPane();
  }

  /**
   * Full pane change: the greeting, progress, questions, and navigation all
   * give way to a single centered thank-you (heading + supporting line),
   * with the visitor's name filled into the heading when we have it.
   */
  #showSuccessPane() {
    const { form, greeting, successMessage, successHeading } = this.refs;

    successHeading.textContent = this.#fillTemplate(successHeading.dataset.headingTemplate || '');

    greeting.classList.add('hidden');
    form.classList.add('hidden');
    successMessage.classList.remove('hidden');
    successMessage.focus();
  }

  /**
   * @param {FormData} formData
   * @returns {Record<string, unknown>}
   */
  #collectSurveyAnswers(formData) {
    /** @type {Record<string, unknown>} */
    // The brand question has a free-text "other" input alongside the pills;
    // fold it into the same answer list so Klaviyo sees one property.
    const brands = formData.getAll('brands[]');
    const otherBrand = String(formData.get('brand_other') || '').trim();
    if (otherBrand) brands.push(otherBrand);

    const answers = {
      'Items interested in': formData.getAll('items[]'),
      'Species hunted': formData.getAll('species[]'),
      'Hunting style': formData.getAll('styles[]'),
      'Interested in replacing kit': formData.get('replace_kit') || null,
      'Current kit brands': brands,
      'Materials preferred': formData.getAll('materials[]'),
      'Durability vs. lightweight preference': formData.get('durability_preference') || null,
      'Interested in base layering': formData.get('base_layer') || null,
      'Camo or solids preference': formData.get('camo_preference') || null,
      'Camo feedback': String(formData.get('camo_feedback') || '').trim() || null,
      'Where other camo companies fail': String(formData.get('competitor_feedback') || '').trim() || null,
      'Additional comments': String(formData.get('comments') || '').trim() || null,
    };

    if (formData.get('replace_kit') === 'yes' || formData.get('replace_kit') === 'pieces') {
      answers['Shirt size'] = formData.get('shirt_size') || null;
      answers['Pant waist'] = formData.get('pant_waist') || null;
      answers['Pant inseam'] = formData.get('pant_inseam') || null;
      answers['Jacket size'] = formData.get('jacket_size') || null;
    }

    if (formData.get('base_layer') === 'yes') {
      answers['Base layer size'] = formData.get('base_layer_size') || null;
    }

    return answers;
  }

  /**
   * @param {Record<string, unknown>} properties
   * @returns {Record<string, unknown>}
   */
  #omitEmptyValues(properties) {
    return Object.fromEntries(
      Object.entries(properties).filter(([, value]) => {
        if (value == null || value === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      })
    );
  }

  /**
   * Best-effort, fire-and-forget: adds the visitor to the configured
   * Klaviyo list with marketing consent, using the same profile properties
   * (email/name/phone/address/survey answers) sent alongside the survey
   * event. Distinct from the event call above — Klaviyo only accepts a
   * `relationships.list` reference on the subscriptions endpoint, not on
   * events — so list membership and event tracking are necessarily two
   * separate requests.
   *
   * Silently skipped if no List ID is configured; that's a merchant setup
   * gap, not something that should block the survey submission itself.
   *
   * @param {Record<string, unknown>} profileAttributes
   */
  #subscribeToList(profileAttributes) {
    const listId = this.dataset.klaviyoListId;
    if (!listId) return;

    const publicApiKey = this.dataset.klaviyoPublicApiKey;
    if (!publicApiKey) return;

    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.dataset.klaviyoMetricName || document.title,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                ...profileAttributes,
                subscriptions: {
                  email: {
                    marketing: { consent: 'SUBSCRIBED' },
                  },
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: { type: 'list', id: listId },
          },
        },
      },
    };

    fetch(`${KLAVIYO_SUBSCRIBE_URL}?company_id=${encodeURIComponent(publicApiKey)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
    }).catch((error) => {
      console.error('kit-survey-form: Klaviyo list subscribe request failed.', error);
    });
  }

  /**
   * @param {Response} response
   * @returns {Promise<string | undefined>}
   */
  async #parseErrorDetail(response) {
    try {
      const data = await response.json();
      return data?.errors?.[0]?.detail;
    } catch {
      return undefined;
    }
  }

  /** @param {string} [detail] */
  #showError(detail) {
    const { errorMessage, errorMessageText } = this.refs;
    errorMessageText.textContent = detail || errorMessageText.dataset.defaultMessage || errorMessageText.textContent;
    errorMessage.classList.remove('hidden');
    errorMessage.focus();
  }
}

if (!customElements.get('kit-survey-form')) {
  customElements.define('kit-survey-form', KitSurveyForm);
}
